import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { groupsAPI, billsAPI } from '../services/api';
import { Group, Bill } from '../types';
import { AppLayout } from '../components/layout/AppLayout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { GroupCard } from '../components/features/GroupCard';

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState<Group[]>([]);
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [groupsData, billsData] = await Promise.all([
        groupsAPI.getAll(),
        billsAPI.getAll(),
      ]);
      setGroups(groupsData.groups);
      setBills(billsData.bills);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AppLayout>
      {/* Header */}
      <div className="bg-gradient-to-br from-green-600 to-green-700 text-white">
        <div className="px-4 py-6 pb-16">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-green-100 text-sm">Welcome back</p>
              <h1 className="text-2xl font-bold">{user?.name}</h1>
            </div>
            <button
              onClick={logout}
              className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate('/scan')}
              className="flex items-center gap-3 p-4 bg-white/10 hover:bg-white/20 rounded-2xl transition-all active:scale-[0.98]"
            >
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="font-semibold">Scan Bill</p>
                <p className="text-xs text-green-100">Quick split</p>
              </div>
            </button>

            <button
              onClick={() => navigate('/groups/new')}
              className="flex items-center gap-3 p-4 bg-white/10 hover:bg-white/20 rounded-2xl transition-all active:scale-[0.98]"
            >
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="font-semibold">New Group</p>
                <p className="text-xs text-green-100">Create team</p>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Overlapping Card Effect */}
      <div className="px-4 -mt-8 pb-4 space-y-6">
        {/* Recent Bills Section */}
        <section>
          <Card className="overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-slate-900">Recent Bills</h2>
                {bills.length > 0 && (
                  <button
                    onClick={() => navigate('/activity')}
                    className="text-sm text-green-600 font-medium"
                  >
                    View all
                  </button>
                )}
              </div>
            </div>

            {bills.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">No bills yet</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Scan your first bill to get started
                </p>
                <Button onClick={() => navigate('/scan')} size="sm">
                  Scan Bill
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {bills.slice(0, 3).map((bill) => (
                  <div
                    key={bill.id}
                    onClick={() => navigate(`/bills/${bill.id}`)}
                    className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">
                          {bill.restaurant_name || 'Unknown Restaurant'}
                        </p>
                        <p className="text-xs text-slate-500">{bill.group_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">
                          ₹{Number(bill.total).toLocaleString('en-IN')}
                        </p>
                        <p className={`text-xs ${
                          bill.status === 'settled' ? 'text-green-600' :
                          bill.status === 'finalized' ? 'text-blue-600' : 'text-amber-600'
                        }`}>
                          {bill.status}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </section>

        {/* Groups Section */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900">Your Groups</h2>
            <button
              onClick={() => navigate('/groups/new')}
              className="text-sm text-green-600 font-medium flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New
            </button>
          </div>

          {groups.length === 0 ? (
            <Card>
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">No groups yet</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Create a group to start splitting bills
                </p>
                <Button onClick={() => navigate('/groups/new')} size="sm">
                  Create Group
                </Button>
              </div>
            </Card>
          ) : (
            <div className="space-y-3">
              {groups.map((group) => (
                <GroupCard
                  key={group.id}
                  id={group.id}
                  name={group.name}
                  description={group.description}
                  memberCount={group.member_count || 0}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </AppLayout>
  );
};

export default Dashboard;
